'use client';

import {isEmpty} from 'lodash';
import {Skeleton} from '@/components/atoms';
import {css} from '@panda/css';
import {CatalogOffer} from 'epic-graphql-types';
import {AnalyticEventProps, KeyImageType} from '@/types';
import {useMessages} from '@epic-mw/i18n';
import {useCatalogPriceEngine} from './useCatalogPriceEngine';
import {useMediaQuery} from '@/hooks';
import {getTagByCategories} from '@/lib/utils/catalog.utils';
import {CardProps, TagFilledIcon, Stack, Text, IconIllustration} from '@eds-web/react';
import {CatalogCheckoutOfferCard} from './CatalogCheckoutOfferCard.molecule';
import {CatalogLinkOfferCard} from './CatalogLinkOfferCard.molecule';
import {CatalogOfferCardsFieldsFragment, CatalogOfferCardVariant} from '@common/hygraph-schema';
import {CatalogSubOfferCard} from './CatalogSubOfferCard.molecule';

const iconStyle = css({
	position: 'absolute !important',
	inset: 0,
	margin: 'auto'
});

const containerStyle = css({
	'& div:has(.catalog-card-images)': {
		outlineStyle: 'solid',
		outlineOffset: '5px',
		transitionProperty: 'all',
		transitionTimingFunction: 'ease',
		transitionDuration: '200ms',
		outlineWidth: '0',

		position: 'relative',
		height: 'full',
		background: 'linear-gradient(to bottom, rgb(32, 32, 36), rgb(24, 24, 28))',
		_after: {
			transition: `opacity 125ms ease-in-out`,
			content: "''",
			width: '100%',
			height: '100%',
			position: 'absolute',
			top: 0,
			left: 0,
			background: 'white',
			opacity: 0,
			pointerEvents: 'none',
			zIndex: 1,
			borderRadius: 0
		}
	},

	_hover: {
		'& div:has(.catalog-card-images)': {
			outlineOffset: '4px',
			outlineWidth: '4px',
			outlineColor: 'eds.palette.static.white',
			_after: {
				opacity: 0.1
			}
		},

		'& .catalog-card-images img': {
			transform: 'scale(1.02)'
		}
	}
});

interface CatalogOfferCardProps extends CatalogOffer {
	variant?: CatalogOfferCardsFieldsFragment['variant'];
	checkout?: boolean;
	isLoading?: boolean;
	className?: string;
	analytics?: AnalyticEventProps;
	locale: string;
}

export const CatalogOfferCard = ({
	isLoading,
	checkout,
	variant = CatalogOfferCardVariant.Outline,
	analytics,
	locale,
	...offer
}: CatalogOfferCardProps) => {
	const messages = useMessages();
	const price = useCatalogPriceEngine(offer);
	const isMobile = useMediaQuery('sm');

	if (!offer || isEmpty(offer) || isLoading) {
		return <Skeleton style={{borderRadius: '8px'}} width="auto" height="416px" />;
	}

	const category = getTagByCategories(offer.categories);
	const thumbnail = offer.keyImages?.find(
		(image) => image && image.type === KeyImageType.OFFER_IMAGE_TALL
	);

	const cardProps: CardProps = {
		title: null,
		clickOverlayProps: {tabIndex: 0},
		variant: 'ghost',
		imageAspectRatio: '3:4',
		imageMode: 'single',
		images: (
			<div className="catalog-card-images">
				{thumbnail ? (
					<img alt={thumbnail.alt!} src={thumbnail.url!} />
				) : (
					<IconIllustration size="32" className={iconStyle} icon={<TagFilledIcon />} />
				)}
			</div>
		)
	};

	switch (variant) {
		case CatalogOfferCardVariant.Subscribe: {
			return (
				<CatalogSubOfferCard cardProps={cardProps} analytics={analytics} offer={offer} />
			);
		}

		case CatalogOfferCardVariant.Outline: {
			cardProps.className = containerStyle;
			cardProps.title = offer.title;
			cardProps.description = price;
			cardProps.eyebrow = category ? messages[category.messageCode] : '';
			break;
		}

		case CatalogOfferCardVariant.Inline: {
			cardProps.className = containerStyle;
			break;
		}
	}

	// "spend your account balance" cards versus "show more" cards
	const card = checkout ? (
		<CatalogCheckoutOfferCard
			cardProps={cardProps}
			offer={offer}
			analytics={analytics}
			locale={locale}
		/>
	) : (
		<CatalogLinkOfferCard
			cardProps={cardProps}
			offer={offer}
			analytics={analytics}
			locale={locale}
		/>
	);

	// Spend page offer card
	if (variant === CatalogOfferCardVariant.Inline) {
		return (
			<Stack direction="column" gap={0}>
				{card}
				<Stack direction="column" gap={4}>
					<Text variant={isMobile ? 'uiMDBold' : 'headingMD'}>{offer.title}</Text>
					<Text variant={isMobile ? 'uiMD' : 'uiLG'}>{price}</Text>
				</Stack>
			</Stack>
		);
	}

	return card;
};
